import {
  FP_COLUMNS_ATTRIBUTE,
  FP_COLUMN_HEADER_ATTRIBUTE,
  FP_CUSTOM_SOURCE_PREFIX,
  FP_FIELD_SOURCES,
  FP_LINES_FILTERS,
  FP_LINE_FIELD_SOURCES,
  FP_PAGE_NUMBER_FORMATS,
  FP_NOTE_MARKDOWN_ATTRIBUTE,
  FP_SHOW_WHEN_ATTRIBUTE,
  FP_SHOW_WHEN_CONDITIONS,
  FP_TABLE_SCOPES,
  FP_TAGS,
  FP_TOTAL_WEIGHT_SOURCE_PREFIX,
  parseShowWhenValue,
} from "./quoteDocuments";

/**
 * Shared, DOM-free vocabulary rules for quote-document markup. The backend
 * sanitizer and the frontend authoring surfaces both derive their
 * tag/attribute allowlist and binding-value rules from this single source, so
 * the client never authors markup the server will reject.
 */

/** Standard HTML tags a quote document may contain, beyond the fp vocabulary. */
export const ALLOWED_HTML_TAGS = [
  "section",
  "div",
  "p",
  "span",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "small",
  "br",
  "hr",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "header",
  "footer",
  "blockquote",
  "figure",
  "figcaption",
  "template",
] as const;

/** The full tag allowlist: standard tags plus the fp vocabulary. */
export const ALLOWED_TAGS: ReadonlySet<string> = new Set<string>([
  ...ALLOWED_HTML_TAGS,
  ...FP_TAGS,
]);

/** Attributes any element may carry regardless of tag. */
export const GLOBAL_ATTRIBUTES: ReadonlySet<string> = new Set<string>([
  "class",
  "style",
]);

/**
 * Inline `style` property names an author may set. The value is validated
 * separately for unsafe constructs; the property must be on this list so an
 * unknown or dangerous CSS property never reaches a customer's browser.
 */
export const ALLOWED_STYLE_PROPERTIES: ReadonlySet<string> = new Set<string>([
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "color",
  "background-color",
  "text-align",
  "text-decoration",
  "text-transform",
  "letter-spacing",
  "line-height",
  "white-space",
  "transform",
  "width",
  "height",
  "max-width",
  "min-width",
  "max-height",
  "min-height",
  "margin",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "columns",
  "column-count",
  "column-gap",
  "display",
  "page-break-before",
  "page-break-after",
  "page-break-inside",
  "break-before",
  "break-after",
  "break-inside",
]);

/** Substrings that make a style value unsafe (case-insensitive). */
export const UNSAFE_STYLE_TOKENS = [
  "url(",
  "expression(",
  "@import",
  "javascript:",
];

/**
 * Validates an inline `style` value: every declaration's property must be on
 * the allowlist and no value may contain an unsafe construct or control char.
 * @param value - The raw `style` attribute value.
 * @returns The list of style violations, empty when the value is safe.
 */
export function validateStyle(value: string): string[] {
  const lower = value.toLowerCase();
  const violations: string[] = [];

  for (const token of UNSAFE_STYLE_TOKENS) {
    if (lower.includes(token)) {
      violations.push(`unsafe style value (${token})`);
    }
  }
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(value)) {
    violations.push("control character in style value");
  }

  for (const declaration of value.split(";")) {
    const trimmed = declaration.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const separator = trimmed.indexOf(":");
    if (separator === -1) {
      violations.push("malformed style declaration");
      continue;
    }
    const property = trimmed.slice(0, separator).trim().toLowerCase();
    if (property.startsWith("--")) {
      continue;
    }
    if (!ALLOWED_STYLE_PROPERTIES.has(property)) {
      violations.push(`disallowed style property ${property}`);
    }
  }

  return violations;
}

/**
 * `data-*` control attributes the renderer and PDF paginator read. Kept as an
 * explicit set so arbitrary `data-*` attributes cannot smuggle content past
 * the allowlist.
 */
export const CONTROL_ATTRIBUTES: ReadonlySet<string> = new Set<string>([
  "data-fp-skip-header",
  "data-fp-skip-first-header",
  "data-fp-skip-footer",
  "data-fp-repeat",
  "data-fp-fit-page",
  "data-fp-node",
  "data-pdf-keep-together",
  FP_COLUMNS_ATTRIBUTE,
  FP_COLUMN_HEADER_ATTRIBUTE,
  FP_NOTE_MARKDOWN_ATTRIBUTE,
  FP_SHOW_WHEN_ATTRIBUTE,
]);

/** The `fp-lines` and `fp-table` attribute selecting lines by type. */
const FP_LINES_FILTER_ATTRIBUTE = "filter";

/** The `fp-table` attribute deciding how the table repeats the quote's lines. */
const FP_TABLE_SCOPE_ATTRIBUTE = "scope";

/** The `fp-table` attribute repeating the column-header band on every page. */
export const FP_TABLE_REPEAT_HEADER_ATTRIBUTE = "repeat-header";

/** The `fp-table` attribute colouring interior rules independently of the border. */
const FP_RULE_COLOR_ATTRIBUTE = "rule-color";

/** The `fp-table` attribute zebra-striping every other body row. */
const FP_STRIPE_ATTRIBUTE = "stripe";

/**
 * The `fp-column` attributes styling the header band independently of the body
 * cells, so a header can be a reversed-out claret band over plain rows.
 */
const FP_COLUMN_HEADER_STYLE_ATTRIBUTES = [
  "header-background",
  "header-color",
  "header-size",
  "header-decoration",
] as const;

/** Attributes whose value must resolve to a colour, on whichever tag carries them. */
const COLOR_VALUED_ATTRIBUTES: ReadonlySet<string> = new Set<string>([
  FP_RULE_COLOR_ATTRIBUTE,
  FP_STRIPE_ATTRIBUTE,
  "header-background",
  "header-color",
]);

/**
 * Container attributes every tag of the fp vocabulary accepts. Each value is a
 * CSS value.
 */
export const FP_CONTAINER_ATTRIBUTES = [
  "padding",
  "margin",
  "background",
  "border",
  "border-top",
  "border-right",
  "border-bottom",
  "border-left",
  "radius",
  "width",
  "align",
  "gap",
  "size",
  "color",
  "font",
  "weight",
  "font-style",
  "decoration",
  "transform",
  "tracking",
  "height",
  "min-height",
  "max-height",
  "max-width",
  "min-width",
] as const;

/** One container attribute name. */
export type FpContainerAttribute = (typeof FP_CONTAINER_ATTRIBUTES)[number];

/**
 * Builds a tag's attribute allowlist: the container attributes plus its own.
 * @param own - The tag-specific attribute names
 * @returns The attribute allowlist for the tag
 */
export function withContainerAttributes(
  ...own: readonly string[]
): ReadonlySet<string> {
  return new Set<string>([...FP_CONTAINER_ATTRIBUTES, ...own]);
}

/** The `fp-page-number` attribute choosing the counter's shape. */
export const FP_PAGE_NUMBER_FORMAT_ATTRIBUTE = "format";

/** Per-fp-tag attribute allowlist, on top of the global attributes. */
export const FP_TAG_ATTRIBUTES: Record<string, ReadonlySet<string>> = {
  "fp-field": withContainerAttributes("source", "hide-empty"),
  "fp-markdown": new Set(["source"]),
  "fp-image": withContainerAttributes("asset-id"),
  // The counters take `margin` alone rather than the full container set: they
  // print one short value inline, so spacing is the only box prop that means
  // anything on them.
  "fp-page-number": new Set([FP_PAGE_NUMBER_FORMAT_ATTRIBUTE, "margin"]),
  "fp-page-break": new Set<string>([]),
  "fp-lines": new Set([FP_LINES_FILTER_ATTRIBUTE, "number-source"]),
  "fp-breaks": new Set<string>([]),
  "fp-line-field": withContainerAttributes("source", "hide-empty"),
  "fp-page-count": new Set<string>(["margin"]),
  "fp-text": withContainerAttributes("source"),
  "fp-columns": withContainerAttributes("widths"),
  "fp-table": withContainerAttributes(
    FP_TABLE_SCOPE_ATTRIBUTE,
    FP_LINES_FILTER_ATTRIBUTE,
    "break-mode",
    "header-style",
    FP_TABLE_REPEAT_HEADER_ATTRIBUTE,
    "rules",
    FP_RULE_COLOR_ATTRIBUTE,
    FP_STRIPE_ATTRIBUTE,
    "number-source",
  ),
  "fp-column": withContainerAttributes(
    "source",
    "header",
    "label",
    "prefix",
    "suffix",
    "repeat",
    "row",
    "span",
    ...FP_COLUMN_HEADER_STYLE_ATTRIBUTES,
  ),
  "fp-row": withContainerAttributes(),
  "fp-cell": withContainerAttributes("span", "rowspan"),
  "fp-divider": withContainerAttributes(),
  "fp-section-heading": withContainerAttributes(),
  "fp-section-notes": withContainerAttributes(),
  "fp-header": withContainerAttributes(),
  "fp-footer": withContainerAttributes(),
  "fp-page-frame": new Set(["border", "margin", "radius"]),
};

/** The repeater tags whose single `<template>` child is stamped per data item. */
export const REPEATER_TAGS: ReadonlySet<string> = new Set<string>([
  "fp-lines",
  "fp-breaks",
]);

/**
 * fp tags that render entirely from their own attributes and hold no authored
 * content. Anything nested inside one only ever got there because the parser
 * treated an unclosed tag as an open element and swallowed its siblings, so
 * content here is corruption rather than authoring. Membership is by tag name
 * alone, so a tag may be listed before the vocabulary grows it.
 */
export const FP_VOID_TAGS: ReadonlySet<string> = new Set<string>([
  "fp-field",
  "fp-image",
  "fp-line-field",
  "fp-page-break",
  "fp-page-count",
  "fp-page-number",
]);

/**
 * Matches an XML-style self-closing `fp-*` tag. The attribute list is spelled
 * out rather than skipped over so a quoted value may hold `/` or `>` (a url, a
 * data uri, `source="custom:a/b"`) without ending the tag early, and so a `/` that
 * is really the last character of an unquoted value is left alone - HTML reads
 * `<fp-image asset-id=a/>` as the value `a/`, not as a self-closing tag.
 */
const SELF_CLOSING_FP_TAG =
  /<(fp-[a-z0-9-]*)((?:\s+[^\s/=>"'<]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+(?=[\s>])))?)*)\s*\/>/gi;

/**
 * Rewrites self-closing `fp-*` tags into an explicit open/close pair. The HTML
 * parser ignores the trailing slash on a custom element, so `<fp-field ... />`
 * would otherwise become an open element that swallows every following sibling
 * as its children. This must run before any parse of stored document HTML -
 * once a parsed tree is serialized, the swallowed siblings are nested for good.
 * @param html - The raw document HTML
 * @returns The HTML with self-closing fp-* tags expanded
 */
export function normalizeSelfClosingFpTags(html: string): string {
  return html.replace(SELF_CLOSING_FP_TAG, "<$1$2></$1>");
}

/** Maximum depth of nested repeaters (section grouping needs three levels). */
export const MAX_REPEATER_NESTING_DEPTH = 4;

/** Table cell tags that may carry span attributes. */
export const CELL_SPAN_TAGS: ReadonlySet<string> = new Set<string>([
  "td",
  "th",
]);

/**
 * Validates the value of an fp-tag binding attribute against the shared
 * source/condition/filter/scope catalogs and prefixes.
 * @param tag - The lowercased fp tag the attribute belongs to
 * @param name - The lowercased attribute name
 * @param value - The attribute value
 * @param lineScoped - Whether the element sits inside a repeater template
 * @returns The violations for the value, empty when it is valid
 */
export function validateFpAttributeValue(
  tag: string,
  name: string,
  value: string,
  lineScoped: boolean,
): string[] {
  const unsafe = unsafeContainerValueViolations(tag, name, value);
  if (unsafe.length > 0) {
    return unsafe;
  }
  if (
    (tag === "fp-lines" || tag === "fp-table") &&
    name === FP_LINES_FILTER_ATTRIBUTE
  ) {
    return LINES_FILTERS.has(value)
      ? []
      : [`unknown ${tag} ${FP_LINES_FILTER_ATTRIBUTE} "${value}"`];
  }
  if (tag === "fp-page-number" && name === FP_PAGE_NUMBER_FORMAT_ATTRIBUTE) {
    return PAGE_NUMBER_FORMATS.has(value)
      ? []
      : [`unknown fp-page-number ${FP_PAGE_NUMBER_FORMAT_ATTRIBUTE} "${value}"`];
  }
  if (tag === "fp-table" && name === FP_TABLE_SCOPE_ATTRIBUTE) {
    return TABLE_SCOPES.has(value)
      ? []
      : [`unknown ${tag} ${FP_TABLE_SCOPE_ATTRIBUTE} "${value}"`];
  }
  if ((tag === "fp-lines" || tag === "fp-table") && name === "number-source") {
    return isCustomSource(value)
      ? []
      : [`invalid ${tag} number-source "${value}"`];
  }
  if (COLOR_VALUED_ATTRIBUTES.has(name)) {
    return isColorValue(value) ? [] : [`invalid ${tag} ${name} "${value}"`];
  }
  if (COLOR_VALUED_ATTRIBUTES.has(name)) {
    return isColorValue(value) ? [] : [`invalid ${tag} ${name} "${value}"`];
  }
  if (name === "source") {
    return validateSource(tag, value, lineScoped);
  }
  return [];
}

/**
 * Validates a `data-fp-show-when` value: one or more `&&`-joined conditions,
 * each a named condition or a field source whose non-empty value shows the
 * element, and each optionally inverted by the `!` negation prefix. Every
 * conjunct is checked, so an unknown name anywhere in the value is a rejection.
 * @param value - The attribute value
 * @param lineScoped - Whether the element sits inside a repeater template
 * @returns The violations for the value, empty when it is valid
 */
export function validateShowWhen(value: string, lineScoped: boolean): string[] {
  return parseShowWhenValue(value).flatMap(({ condition }) =>
    conditionResolves(condition, lineScoped)
      ? []
      : [`unknown ${FP_SHOW_WHEN_ATTRIBUTE} "${value}"`],
  );
}

/**
 * Whether one bare show-when condition names something the renderer can resolve.
 * @param condition - The condition with any negation prefix already stripped
 * @param lineScoped - Whether the element sits inside a repeater template
 * @returns True when the condition resolves in that scope
 */
function conditionResolves(condition: string, lineScoped: boolean): boolean {
  if (condition === "") {
    return false;
  }
  return (
    SHOW_WHEN_CONDITIONS.has(condition) ||
    isQuoteFieldSource(condition) ||
    (lineScoped && LINE_FIELD_SOURCES.has(condition))
  );
}

const CONTAINER_ATTRIBUTE_NAMES = new Set<string>(FP_CONTAINER_ATTRIBUTES);
const QUOTE_FIELD_SOURCES = new Set<string>(FP_FIELD_SOURCES);
const LINE_FIELD_SOURCES = new Set<string>(FP_LINE_FIELD_SOURCES);
const SHOW_WHEN_CONDITIONS = new Set<string>(FP_SHOW_WHEN_CONDITIONS);
const LINES_FILTERS = new Set<string>(FP_LINES_FILTERS);
const PAGE_NUMBER_FORMATS = new Set<string>(FP_PAGE_NUMBER_FORMATS);
const TABLE_SCOPES = new Set<string>(FP_TABLE_SCOPES);

/**
 * Rejects a container attribute value carrying an unsafe CSS construct. The
 * renderer writes these values straight into inline style, so they need the
 * same guard `validateStyle` applies to an authored `style` attribute.
 * @param tag - The lowercased fp tag carrying the attribute
 * @param name - The lowercased attribute name
 * @param value - The attribute value
 * @returns The violations for the value, empty when it is safe
 */
function unsafeContainerValueViolations(
  tag: string,
  name: string,
  value: string,
): string[] {
  if (!CONTAINER_ATTRIBUTE_NAMES.has(name)) {
    return [];
  }
  const lower = value.toLowerCase();
  return UNSAFE_STYLE_TOKENS.filter((token) => lower.includes(token)).map(
    (token) => `unsafe ${tag} ${name} value (${token})`,
  );
}

/** Hex, a bare colour keyword, or a colour-producing CSS function call. */
const CSS_COLOR_PATTERN =
  /^(#[0-9a-f]{3,8}|[a-z]+|(?:rgb|rgba|hsl|hsla|var)\([^()]*\))$/i;

/**
 * Whether a value resolves to a safe CSS colour.
 * @param value - The attribute value
 * @returns True when the value is a CSS colour
 */
function isColorValue(value: string): boolean {
  return CSS_COLOR_PATTERN.test(value.trim());
}

/**
 * Validates a `source` attribute value against the catalog for its scope.
 * @param tag - The lowercased fp tag carrying the source
 * @param value - The source value
 * @param lineScoped - Whether line-scope sources also resolve for the element
 * @returns The violations for the value, empty when it is valid
 */
function validateSource(
  tag: string,
  value: string,
  lineScoped: boolean,
): string[] {
  if (tag === "fp-line-field" || tag === "fp-column") {
    return LINE_FIELD_SOURCES.has(value) || isCustomSource(value)
      ? []
      : [`unknown ${tag} source "${value}"`];
  }
  const lineSourceResolves = lineScoped && LINE_FIELD_SOURCES.has(value);
  const lineScopedTag = tag === "fp-markdown" || tag === "fp-text";
  return isQuoteFieldSource(value) || (lineScopedTag && lineSourceResolves)
    ? []
    : [`unknown ${tag} source "${value}"`];
}

/**
 * Whether a value is a valid quote-scope source: a catalog key, a
 * `custom:<Field Name>` value, or a `total-weight:<unit>` value.
 * @param value - The source value
 * @returns True when the value is a valid quote-scope source
 */
function isQuoteFieldSource(value: string): boolean {
  return (
    QUOTE_FIELD_SOURCES.has(value) ||
    isCustomSource(value) ||
    hasNonEmptySuffix(value, FP_TOTAL_WEIGHT_SOURCE_PREFIX)
  );
}

/**
 * Whether a value is a `custom:<Field Name>` source with a non-empty name.
 * @param value - The source value
 * @returns True when the value is a custom-field source
 */
function isCustomSource(value: string): boolean {
  return hasNonEmptySuffix(value, FP_CUSTOM_SOURCE_PREFIX);
}

/**
 * Whether a value starts with a prefix and carries a non-empty remainder.
 * @param value - The value to test
 * @param prefix - The required prefix
 * @returns True when the value is the prefix plus a non-empty remainder
 */
function hasNonEmptySuffix(value: string, prefix: string): boolean {
  return (
    value.startsWith(prefix) && value.slice(prefix.length).trim().length > 0
  );
}
